import {
  deleteDocument,
  getDocumentsList,
  postDocument,
} from '$api/patientList/documents';
import {ToastType} from '$components/Toast/constant';
import toast from '$utils/toast';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';

export interface DeleteDocumentVariables {
  documentId: number;
  onSuccess?: () => void;
}

export const useDocumentsHook = (patientId: string) => {
  const queryClient = useQueryClient();

  const {isPending, isError, error, data} = useQuery({
    queryKey: ['documents', patientId],
    queryFn: () => getDocumentsList({patientId}),
  });

  const {mutate: deleteDocumentMutation, isPending: isDocumentDeleting} =
    useMutation({
      mutationKey: ['documents', patientId],
      mutationFn: ({documentId}: DeleteDocumentVariables) =>
        deleteDocument({documentId}),
      onSuccess: (data, {onSuccess}) => {
        queryClient.invalidateQueries({queryKey: ['documents', patientId]});
        if (onSuccess) {
          onSuccess();
        }
        return toast(ToastType.Success, 'Delete document', data.message);
      },
      onError: error => {
        return toast(ToastType.Error, 'Delete document', error.message);
      },
    });

  const {mutate: uploadDocument} = useMutation({
    mutationKey: ['documents', patientId],
    mutationFn: ({
      patientId,
      document,
      name,
      performedBy,
    }: {
      patientId: string;
      document: File;
      name: string;
      performedBy: string;
    }) =>
      postDocument({
        patientId,
        document,
        name,
        performedBy,
      }),
    onSuccess: data => {
      queryClient.invalidateQueries({queryKey: ['documents', patientId]});
      return toast(ToastType.Success, 'Upload document', data.message);
    },
    onError: error => {
      return toast(ToastType.Error, 'Upload document', error.message);
    },
  });

  const uploadDocumentRequest = (
    patientId: string,
    document: File,
    name: string,
    performedBy: string
  ) => {
    uploadDocument({patientId, document, name, performedBy});
  };

  const deleteDocumentRequest = ({
    documentId,
    onSuccess,
  }: DeleteDocumentVariables) => {
    deleteDocumentMutation({
      documentId,
      onSuccess,
    });
  };

  return {
    isPending,
    isError,
    error,
    data,
    deleteDocumentRequest,
    isDocumentDeleting,
    uploadDocumentRequest,
  };
};
